import { Box, Button, Card, Group, Stack, Text } from '@mantine/core';
import { IconPalette } from '@tabler/icons-react';
import type { PfileRow } from '@lib2/model/pfile/PfileTypes';
import { createPfileIds } from '@lib2/model/pfile/PfileUtil';
import { PfileImageUploader } from '@/shared/components/PfileImageUploader';
import type { PfileImageSpec } from '@/lib/constants/pfileImage';
import { BRAND_KIT_COPY } from '@/lib/constants/appFactory';
import { APP_WEB_IMAGE_SPECS_WITH_HOOKS } from '../webLogoSpec';
import { APP_QUERY_KEYS } from '@/lib/constants/app';

interface AppImagesTabProps {
  appCfgId: number;
  cId: number;
  pfiles: PfileRow[];
  title?: string;
  description?: string;
  specs?: PfileImageSpec[];
  /** Opens the app's Brand Kit tab, where the Home image is set (3.46.0). Absent = no pointer. */
  onOpenBrandKit?: () => void;
}

/**
 * The Web App's own images (3.46.0 layout): one row per slot in a grid whose
 * five rows — label, help, preview, size line, actions — line up across the
 * columns (CSS subgrid), every slot with its help, and each preview drawn on
 * the surface the app shows it on. The Home image is not a web upload: it is
 * the app's, set in the Brand Kit, and the pointer card says so.
 */
export function AppImagesTab({
  appCfgId,
  cId,
  pfiles,
  title = 'Web App Images',
  description = 'Upload images for your web application. All images are processed to WebP format.',
  // the Logo slot carries the fit-and-pad hooks (plan 2.7); the other slots are the plain constants
  specs = APP_WEB_IMAGE_SPECS_WITH_HOOKS,
  onOpenBrandKit,
}: AppImagesTabProps) {
  const invalidateQueryKeys = [
    APP_QUERY_KEYS.detail(cId, appCfgId),
    APP_QUERY_KEYS.list(cId),
  ];

  return (
    <Stack gap="lg">
      <Text fw={600} size="lg">{title}</Text>
      <Text size="sm" c="dimmed">
        {description}
      </Text>

      {onOpenBrandKit && (
        <Card withBorder radius="md" p="md" data-testid="web-home-image-pointer">
          <Group gap="md" justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <IconPalette size={20} style={{ flex: 'none' }} />
              <Stack gap={2}>
                <Text fw={600} size="md">
                  {BRAND_KIT_COPY.webPointerTitle}
                </Text>
                <Text size="sm" c="dimmed">
                  {BRAND_KIT_COPY.webPointer}
                </Text>
              </Stack>
            </Group>
            <Button size="xs" variant="light" onClick={onOpenBrandKit} data-testid="open-brand-kit">
              {BRAND_KIT_COPY.pointerOpen}
            </Button>
          </Group>
        </Card>
      )}

      {/* One grid: each uploader spans the five rows (subgrid), so the previews
          sit on one line whatever the length of the help above them. */}
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          columnGap: 'var(--mantine-spacing-xl)',
          rowGap: 'var(--mantine-spacing-xs)',
          alignItems: 'start',
        }}
      >
        {specs.map((spec) => (
          <PfileImageUploader
            key={spec.pfileTypeId}
            pfiles={pfiles}
            spec={spec}
            pfileIds={createPfileIds({
              appCfgId,
              pfileTypeId: spec.pfileTypeId,
            })}
            invalidateQueryKeys={invalidateQueryKeys}
            containerHeight={200}
            layout="grid-rows"
          />
        ))}
      </Box>
    </Stack>
  );
}
